import { useRef, useCallback, useEffect } from "react";
import _ from "lodash";

const DEFAULT_SUBJECT_NODE = {
  displaySequence: 1,
  uid: "",
  subText: "",
  gradingType: "",
  code: "",
  depth: 0,
  parent: null,
  updatedAt: "",
  dataSetNodeWrapper: {
    __typename: "StandardBenchmarkWrapper",
    properties: {
      __typename: "BenchmarkProperties",
      isArchived: false,
      isPowerStandard: false,
      weight: 0,
      isGraded: false,
      grades: [],
      tags: [],
    },
  },
  __typename: "PlannerBenchmark",
};

const EMPTY_ARRAY = [];

/**
 * Creates a subject node for PYP standards
 * @param {string} parentId - The parent node ID
 * @param {Object} subject - The subject data
 * @param {string} childId - The initial child node ID
 * @param {Object} plannerElementNodeSet - The planner element node set data to copy into `dataSetNodeWrapper.standardSet`
 * @returns {Object} - The subject node
 */
const createSubjectNode = ({
  parentId,
  subject,
  childId,
  plannerElementNodeSet,
}) => {
  const {
    //for debugging, if subject.name is not found in resolvedMinimalTree, or node
    //then its subjectName would be shown as Untitled, to quickly identify the issue.
    name: subjectName = "Untitled",
    id: subjectId,
    isArchived = false,
  } = subject;
  return {
    ...DEFAULT_SUBJECT_NODE,
    id: parentId,
    label: subjectName,
    children: [childId],
    subject: subjectId,
    dataSetNodeWrapper: {
      standardSet: {
        ...plannerElementNodeSet,
      },
      properties: {
        ...DEFAULT_SUBJECT_NODE.dataSetNodeWrapper.properties,
        subject: {
          id: subjectId,
          name: subjectName,
          isArchived,
        },
      },
    },
  };
};

/**
 * Hook to manage ancestor tree for selected standards.
 * Maintains a flat map of id:node for efficient lookups and updates.
 *
 * @param {Object} params - Hook parameters
 * @param {Array} params.initialNodes - Initial resolved value from parent containing nodes array
 * @param {boolean} params.isPypStandards - Flag to indicate if PYP standards mode is enabled
 * @returns {Object} - { updateTree, getAncestorTree }
 */
const useTreeManager = ({ initialNodes, isPypStandards, treeMapRef }) => {
  const subjectNodeIdsRef = useRef(new Set());

  if (treeMapRef && !treeMapRef.current.map) {
    treeMapRef.current.map = new Map();
  }

  useEffect(() => {
    if (treeMapRef && !_.isEmpty(initialNodes)) {
      const treeMap = new Map();

      _.forEach(initialNodes, node => {
        const nodeId = _.get(node, "id");
        if (nodeId) {
          treeMap.set(nodeId, node);
        }
      });

      treeMapRef.current.map = treeMap;
    }
  }, [initialNodes, treeMapRef]);

  /**
   * Recursively adds a node and its ancestors to the tree map
   * @param {string} nodeId - The node ID to add
   * @param {Map} nodeMap - Map of all available nodes
   * @param {Map} treeMap - The current tree map
   */
  const addNodeWithAncestors = useCallback((nodeId, nodeMap, treeMap) => {
    if (!nodeId || !nodeMap.has(nodeId)) {
      return;
    }

    const node = nodeMap.get(nodeId);
    treeMap.set(nodeId, node);

    const parentId = node?.parent || _.get(node, "parentData.id");

    if (!parentId) {
      return;
    }

    if (nodeMap.has(parentId) && !treeMap.has(parentId)) {
      addNodeWithAncestors(parentId, nodeMap, treeMap);
    } else if (!nodeMap.has(parentId) && isPypStandards) {
      subjectNodeIdsRef.current.add(parentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Recursively removes a node and its orphaned ancestors from the tree map
   * @param {string} nodeId - The node ID to remove
   * @param {Map} treeMap - The current tree map
   * @param {Set} selectedStandards - Set of currently selected standard IDs
   */
  const removeNodeWithOrphanedAncestors = useCallback(
    (nodeId, treeMap, selectedStandards) => {
      if (!nodeId || !treeMap.has(nodeId)) {
        return;
      }

      const node = treeMap.get(nodeId);
      const parentId = node?.parent || _.get(node, "parentData.id");

      treeMap.delete(nodeId);

      if (!parentId || !treeMap.has(parentId)) {
        return;
      }

      const parentNode = treeMap.get(parentId);
      const parentChildren = _.get(parentNode, "children", []);
      const isParentTagged = selectedStandards.has(parentId);

      if (isParentTagged) {
        return;
      }

      const hasActiveChildren = _.some(
        parentChildren,
        childId => selectedStandards.has(childId) || treeMap.has(childId)
      );

      if (!hasActiveChildren) {
        removeNodeWithOrphanedAncestors(parentId, treeMap, selectedStandards);
      }
    },
    []
  );

  /**
   * Updates the tree map by adding/removing nodes
   * @param {Object} params - Update parameters
   * @param {Array} params.addedNodes - Array of node IDs to add
   * @param {Array} params.removedNodes - Array of node IDs to remove
   * @param {Map} params.nodeMap - Map of all available nodes
   * @param {Set} params.selectedNodes - Set of currently selected node IDs
   */
  const updateTree = useCallback(
    ({ addedNodes = [], removedNodes = [], nodeMap, selectedNodes = [] }) => {
      if (_.isNil(nodeMap) || !treeMapRef) {
        return;
      }

      const treeMap = treeMapRef.current.map;

      _.forEach(addedNodes, nodeId => {
        addNodeWithAncestors(nodeId, nodeMap, treeMap);
      });

      _.forEach(removedNodes, nodeId => {
        removeNodeWithOrphanedAncestors(nodeId, treeMap, selectedNodes);
      });
    },
    [addNodeWithAncestors, removeNodeWithOrphanedAncestors]
  );

  /**
   * Builds and returns the ancestor tree array.
   * For PYP standards, creates synthetic subject nodes for orphaned parents.
   * @returns {Array} - Array of tree nodes
   */
  const getAncestorTree = useCallback(() => {
    if (!treeMapRef) return EMPTY_ARRAY;
    const treeMap = treeMapRef.current.map;

    if (!isPypStandards) {
      return Array.from(treeMap.values());
    }

    const subjectNodeIds = subjectNodeIdsRef.current;
    const subjectNodesMap = new Map();

    treeMap.forEach(node => {
      const parentId = node?.parent || _.get(node, "parentData.id");

      if (!parentId || !subjectNodeIds.has(parentId)) {
        return;
      }

      const subject = _.get(node, "dataSetNodeWrapper.properties.subject", {});
      const plannerElementNodeSet =
        _.get(node, "dataSetNodeWrapper.plannerElementNodeSet") ||
        _.get(node, "dataSetNodeWrapper.standardSet") ||
        _.get(node, "standardsWrapper.standardSet");

      if (subjectNodesMap.has(parentId)) {
        subjectNodesMap.get(parentId).children.push(node.id);
      } else {
        const subjectNode = createSubjectNode({
          parentId,
          subject,
          childId: node.id,
          plannerElementNodeSet,
        });
        subjectNodesMap.set(parentId, subjectNode);
      }
    });

    subjectNodesMap.forEach((node, nodeId) => {
      treeMap.set(nodeId, node);
    });

    return Array.from(treeMap.values());
  }, [isPypStandards]);

  if (treeMapRef) treeMapRef.current.getAncestorTree = getAncestorTree;

  return {
    updateTree,
    getAncestorTree,
  };
};

export default useTreeManager;
